import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSetAtom, useAtom } from 'jotai';
import { Button, Form, Input, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import useKpi from '@hooks/useKPI';
import { clearCache } from '@src/api/cacheAdapter/config';
import { refreshTrends } from '@store/common/actions';
import FieldElement from './FieldElement';
import { CREATE_NEW_SPI_CONFIG } from '@src/utils/kpi';
import kpiStore from '@src/globalStore/kpiStore';
import { currentKpiIdAtom } from '@src/atoms/panels';
import { validateKPIConfigData } from '../utils';
import { selectedKpiThresholdsAtom, editModeAtom } from '../atoms';
import {
  StyledForm,
  StyledKPIWrapper,
  StyledBody,
  StyledFooter,
  StyledName,
  StyledTop,
  StyledGoBackButton,
  StyledCaption,
} from '../styled';

const EditPanel = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const setIsKpiDrawerOpen = kpiStore((state: any) => state.setIsKpiDrawerOpen);
  const setCurrentKpiId = useSetAtom(currentKpiIdAtom);
  const kpiList = kpiStore((state: any) => state.kpiList);
  const kpiMetaConfigs = kpiStore((state: any) => state.kpiMetaConfigs);
  const setEditMode = useSetAtom(editModeAtom);
  const { saveKpiThresholdsMutation, updateKpiThresholdsMutation } = useKpi();
  const [selectedKpiThresholds, setSelectedKpiThresholds] = useAtom(selectedKpiThresholdsAtom);

  const onCancelHandler = () => {
    onGoBackHandler();
    setIsKpiDrawerOpen(false);
    setSelectedKpiThresholds(CREATE_NEW_SPI_CONFIG);
  };

  const onGoBackHandler = () => {
    setEditMode(false);
    setSelectedKpiThresholds(CREATE_NEW_SPI_CONFIG);
  };

  const onKpiTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedKpiThresholds({ ...selectedKpiThresholds, kpiName: e.target.value });
  };

  const onUpdateAndApplyHandler = async () => {
    const validation = await validateKPIConfigData(selectedKpiThresholds, kpiList);
    if (validation.status) {
      setIsLoading(true);
      await updateKpiThresholdsMutation.mutate(selectedKpiThresholds, {
        onSuccess: async () => {
          // Update store with latest config so metric API gets it, then update atom
          const currentList = kpiStore.getState().kpiList;
          const updatedList = currentList.map((k: any) =>
            k.kpiID === selectedKpiThresholds.kpiID ? { ...selectedKpiThresholds } : k,
          );
          kpiStore.getState().setKPIs(updatedList);
          setCurrentKpiId(selectedKpiThresholds.kpiID);
          message.info(
            `${selectedKpiThresholds.kpiName} KPI has been updated and applied Successfully.`,
          );
          setIsLoading(false);
          clearCache();
          // Defer so Jotai/Zustand updates flush and components re-render with new config before metric API runs
          dispatch(refreshTrends());
          onGoBackHandler();
          setIsKpiDrawerOpen(false);
        },
        onError: () => {
          message.error('There was an error while updating KPI. Please try again later.');
          setIsLoading(false);
        },
      });
    } else {
      message.error(validation.errorMessage);
    }
  };

  const onCreateAndApplyHandler = async () => {
    const validation = await validateKPIConfigData(selectedKpiThresholds, kpiList);
    if (validation.status) {
      setIsLoading(true);
      saveKpiThresholdsMutation.mutate(selectedKpiThresholds, {
        onSuccess: (response: any) => {
          const newKpiId = response?.data?.kpiID;
          if (newKpiId != null) {
            // Update atoms and store so metric API gets latest config: add new KPI to store then set selected id
            const newKpiConfig =
              response?.data && typeof response.data === 'object'
                ? { ...response.data }
                : { ...selectedKpiThresholds, kpiID: newKpiId };
            const currentList = kpiStore.getState().kpiList;
            if (!currentList.some((k: any) => k.kpiID === newKpiId)) {
              kpiStore.getState().setKPIs([...currentList, newKpiConfig]);
            }
            setCurrentKpiId(newKpiId);
          }
          message.info(
            `${selectedKpiThresholds.kpiName} KPI has been created and applied Successfully.`,
          );
          setIsLoading(false);
          clearCache();
          dispatch(refreshTrends());
          onGoBackHandler();
          setIsKpiDrawerOpen(false);
        },
        onError: () => {
          message.error('There was an error while creating KPI. Please try again later.');
          setIsLoading(false);
        },
      });
    } else {
      message.error(validation.errorMessage);
    }
  };

  return (
    <>
      <StyledBody>
        <StyledKPIWrapper>
          <StyledTop>
            <p>Edit KPI</p>
            <StyledGoBackButton onClick={() => onGoBackHandler()}>
              <ArrowLeftOutlined /> Go Back
            </StyledGoBackButton>
          </StyledTop>
          <StyledName>
            <label>KPI Name</label>
            <Input
              value={selectedKpiThresholds?.kpiName ?? ''}
              onChange={(e) => onKpiTextChange(e)}
            />
          </StyledName>
        </StyledKPIWrapper>
        <StyledCaption>
          <p>A minute is considered good if</p>
        </StyledCaption>
        <StyledForm>
          <Form layout="horizontal">
            {Object.keys(kpiMetaConfigs).map((thresholdKey, index) => {
              return (
                <FieldElement
                  key={thresholdKey}
                  kpiIndex={index}
                  thresholdKey={thresholdKey}
                  thresholdValue={selectedKpiThresholds[thresholdKey]}
                />
              );
            })}
          </Form>
        </StyledForm>
      </StyledBody>
      <StyledFooter>
        <Button type="primary" onClick={onCancelHandler} ghost>
          Cancel
        </Button>
        {selectedKpiThresholds && selectedKpiThresholds.kpiID ? (
          <Button type="primary" onClick={onUpdateAndApplyHandler} loading={isLoading}>
            Update and Apply KPI
          </Button>
        ) : (
          <Button type="primary" onClick={onCreateAndApplyHandler} loading={isLoading}>
            Create and Apply KPI
          </Button>
        )}
      </StyledFooter>
    </>
  );
};

export default EditPanel;
